import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTrash2, FiLink, FiAlertCircle, FiCheck, FiArrowRight, FiLayers, FiSearch, FiExternalLink } = FiIcons;

const ManageConnections = () => {
  const [elements, setElements] = useState([]);
  const [connections, setConnections] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [elementClusters, setElementClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Form State
  const [sourceElement, setSourceElement] = useState('');
  const [targetElement, setTargetElement] = useState('');
  const [weighting, setWeighting] = useState('middle');

  // Filter State
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [elementsRes, connectionsRes, clustersRes, relationsRes] = await Promise.all([
        apiService.getElements(),
        apiService.getElementConnections(),
        apiService.getClusters(),
        apiService.getClusterElementRelations()
      ]);
      
      setElements(elementsRes.data || []);
      setConnections(connectionsRes.data || []);
      
      if (clustersRes.status === 'success') setClusters(clustersRes.data);
      if (relationsRes.status === 'success') setElementClusters(relationsRes.data);

    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Daten.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConnection = async (e) => {
    e.preventDefault();
    if (!sourceElement || !targetElement) {
      setMessage({ type: 'error', text: 'Bitte wählen Sie beide Elemente aus.' });
      return;
    }

    if (sourceElement === targetElement) {
      setMessage({ type: 'error', text: 'Ein Element kann nicht mit sich selbst verbunden werden.' });
      return;
    }

    try {
      await apiService.createElementConnection({
        element_id: parseInt(sourceElement),
        connection_element_id: parseInt(targetElement),
        weighting: weighting
      });
      
      setMessage({ type: 'success', text: 'Verbindung erfolgreich erstellt.' });
      setSourceElement('');
      setTargetElement('');
      setWeighting('middle');
      fetchData();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error creating connection:', error);
      setMessage({ type: 'error', text: 'Fehler beim Erstellen der Verbindung.' });
    }
  };

  const handleDeleteConnection = async (id) => {
    if (!window.confirm('Möchten Sie diese Verbindung wirklich löschen?')) return;

    try {
      await apiService.deleteElementConnection(id);
      setMessage({ type: 'success', text: 'Verbindung erfolgreich gelöscht.' });
      fetchData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting connection:', error);
      setMessage({ type: 'error', text: 'Fehler beim Löschen der Verbindung.' });
    }
  };

  const getClusterForElement = (elementId) => {
    const relation = elementClusters.find(r => r.element_id === elementId);
    if (!relation) return null;
    return clusters.find(c => c.id === relation.cluster_id);
  };

  const getElementName = (id) => {
    const element = elements.find(e => e.id === id);
    return element ? element.title : `ID: ${id}`;
  };

  const getWeightingLabel = (weight) => {
    switch(weight) {
      case 'high': return 'Stark';
      case 'middle': return 'Mittel';
      case 'low': return 'Schwach';
      default: return weight;
    }
  };

  const getWeightingBadge = (weight) => {
    switch(weight) {
      case 'high': 
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">Stark</span>;
      case 'middle': 
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">Mittel</span>;
      case 'low': 
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">Schwach</span>;
      default: 
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Unbekannt</span>;
    }
  };

  // Helper Component for Cluster Badge
  const ClusterBadge = ({ elementId }) => {
    const cluster = getClusterForElement(elementId);
    if (!cluster) return <span className="text-xs text-gray-400 italic">(Kein Cluster)</span>;
    return (
      <span className="flex items-center text-xs text-brand-secondary bg-brand-primary/5 px-2 py-0.5 rounded border border-brand-primary/10">
        <SafeIcon icon={FiLayers} className="w-3 h-3 mr-1" />
        {cluster.title}
      </span>
    );
  };

  // Prepare grouped elements for select dropdowns
  const getGroupedOptions = () => {
    const groups = {};
    const noCluster = [];
    
    // Initialize groups
    clusters.forEach(c => {
        groups[c.id] = { ...c, elements: [] };
    });

    elements.forEach(e => {
        const cluster = getClusterForElement(e.id);
        if (cluster && groups[cluster.id]) {
            groups[cluster.id].elements.push(e);
        } else {
            noCluster.push(e);
        }
    });

    return { groups: Object.values(groups), noCluster };
  };

  const { groups, noCluster } = getGroupedOptions();

  const renderSelectOptions = () => (
    <>
      <option value="">Bitte wählen...</option>
      {groups.map(group => (
        group.elements.length > 0 && (
          <optgroup key={group.id} label={group.title}>
            {group.elements.map(el => (
              <option key={el.id} value={el.id}>{el.title}</option>
            ))}
          </optgroup>
        )
      ))}
      {noCluster.length > 0 && (
        <optgroup label="Nicht zugeordnet">
          {noCluster.map(el => (
            <option key={el.id} value={el.id}>{el.title}</option>
          ))}
        </optgroup>
      )}
    </>
  );

  // Filter connections based on search term
  const filteredConnections = connections.filter(conn => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const sourceName = getElementName(conn.element_id).toLowerCase();
    const targetName = getElementName(conn.connection_element_id).toLowerCase();
    return sourceName.includes(term) || targetName.includes(term);
  });

  if (loading) return (
    <div className="flex justify-center items-center h-64">
       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
    </div>
  );

  return (
    <div className="space-y-8 w-full">
      
      {/* Header with Public Link */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div>
           <h2 className="text-xl font-semibold text-gray-900">Verbindungen verwalten</h2>
           <p className="text-sm text-gray-500">Definieren Sie Abhängigkeiten zwischen Elementen für das Netzwerk-Diagramm.</p>
        </div>
        <a
          href="#/public/connections"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors border border-gray-300"
        >
          <SafeIcon icon={FiExternalLink} className="mr-2" /> Öffentliche Seite
        </a>
      </div>

      {/* Creation Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center">
          <div className="p-2 bg-brand-secondary rounded-lg text-white mr-3">
             <SafeIcon icon={FiPlus} className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Neue Verbindung erstellen</h3>
          </div>
        </div>
        
        <form onSubmit={handleCreateConnection} className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
            
            {/* Source */}
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ausgangselement (Ursache)
              </label>
              <div className="relative">
                <select
                  value={sourceElement}
                  onChange={(e) => setSourceElement(e.target.value)}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary pl-3 pr-10 py-2 border"
                  required
                >
                  {renderSelectOptions()}
                </select>
              </div>
            </div>

            {/* Arrow Icon */}
            <div className="hidden md:flex pb-3 text-gray-400">
               <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
            </div>

            {/* Target */}
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Zielelement (Wirkung)
              </label>
              <div className="relative">
                <select
                  value={targetElement}
                  onChange={(e) => setTargetElement(e.target.value)}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary pl-3 pr-10 py-2 border"
                  required
                >
                  {renderSelectOptions()}
                </select>
              </div>
            </div>

            {/* Weighting */}
            <div className="w-full md:w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stärke der Verbindung
              </label>
              <select
                value={weighting}
                onChange={(e) => setWeighting(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary py-2 border"
              >
                <option value="high">Stark (High)</option>
                <option value="middle">Mittel (Middle)</option>
                <option value="low">Schwach (Low)</option>
              </select>
            </div>

            {/* Submit Button */}
            <div className="w-full md:w-auto">
              <button
                type="submit"
                className="w-full inline-flex justify-center items-center px-6 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-secondary hover:bg-brand-dark focus:outline-none transition-colors"
                style={{ height: '42px' }}
              >
                <SafeIcon icon={FiLink} className="mr-2 h-4 w-4" />
                Verbinden
              </button>
            </div>
          </div>
        </form>
      </div>

      {message.text && (
        <div className={`p-4 rounded-lg flex items-center border ${message.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'} animate-fadeIn`}>
          <SafeIcon icon={message.type === 'success' ? FiCheck : FiAlertCircle} className="mr-3 w-5 h-5" />
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      {/* Connections List */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            Bestehende Verbindungen
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
              {filteredConnections.length}
            </span>
          </h3>
          
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary sm:text-sm"
            />
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[40%]">
                  Ausgangselement
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                  Richtung
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[40%]">
                  Zielelement
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredConnections.length > 0 ? (
                filteredConnections.map((conn) => (
                  <tr key={conn.id} className="hover:bg-gray-50 transition-colors">
                    {/* Source */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900 mb-1">{getElementName(conn.element_id)}</span>
                        <ClusterBadge elementId={conn.element_id} />
                      </div>
                    </td>
                    
                    {/* Direction / Weight */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center justify-center space-y-1">
                         <SafeIcon icon={FiArrowRight} className="text-gray-400 w-5 h-5" />
                         {getWeightingBadge(conn.weighting)}
                      </div>
                    </td>
                    
                    {/* Target */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900 mb-1">{getElementName(conn.connection_element_id)}</span>
                        <ClusterBadge elementId={conn.connection_element_id} />
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <button
                        onClick={() => handleDeleteConnection(conn.id)}
                        className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50 transition-colors"
                        title="Verbindung löschen"
                      >
                        <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500 italic">
                    {searchTerm ? 'Keine Verbindungen gefunden.' : 'Noch keine Verbindungen vorhanden. Starten Sie oben mit der ersten Verknüpfung.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageConnections;